import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OTPVerification } from "@/components/OTPVerification";
import { useAuth } from "@/hooks/useAuthState";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { LogOut, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";

export function PhoneVerificationGuard({ children }: { children: React.ReactNode }) {
  const { user, signOut } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [isSavingPhone, setIsSavingPhone] = useState(false);

  // Reset all modal states when user logs out
  useEffect(() => {
    if (!user) {
      setShowOTPModal(false);
      setShowPhoneInput(false);
      setPhone("");
      setPhoneError("");
    }
  }, [user]);

  useEffect(() => {
    // Skip verification for admin users
    if (user && user.role === 'admin') {
      return;
    }

    // PRIORITY 1: Check if user has a phone but it's not verified
    // This must be checked FIRST to prevent flashing the phone input modal
    // for users who already provided their phone during signup
    if (user && user.phone && !user.phoneVerified) {
      // For Google OAuth users, only show verification after profile completion >= 30%
      const isGoogleUser = user.supabaseUser?.app_metadata?.provider === 'google';
      const profileComplete = (user?.profileCompletion ?? 0) >= 30;

      const shouldShowVerification = isGoogleUser ? profileComplete : true;

      if (shouldShowVerification) {
        // Just show the OTP modal - don't auto-send SMS
        // User must click "Send Code" button to receive the code
        setShowOTPModal(true);
      }
      return; // Early return to prevent showing phone input modal
    }

    // PRIORITY 2: Show phone input modal for users without phone
    // Only reached if user doesn't have a phone number at all
    if (user && !user.phone) {
      // For Google OAuth users, only show after profile completion >= 30%
      const isGoogleUser = user.supabaseUser?.app_metadata?.provider === 'google';
      const profileComplete = (user?.profileCompletion ?? 0) >= 30;
      const shouldShow = isGoogleUser ? profileComplete : true;

      if (shouldShow) {
        setShowPhoneInput(true);
      }
      return;
    }
  }, [user, navigate]); // Depend on user and navigate

  const handleVerified = () => {
    setShowOTPModal(false);
    toast({
      title: "Phone Verified!",
      description: "Your phone number has been verified successfully.",
      variant: "default",
    });
    // Reload to fetch updated user data
    window.location.reload();
  };

  const validatePhoneNumber = (phoneNumber: string): boolean => {
    setPhoneError("");

    // Remove all spaces, dashes, keep only digits and +
    const cleanPhone = phoneNumber.replace(/[\s\-\(\)]/g, '').replace(/[^\d+]/g, '');

    // Count only digits (not +)
    const digitsOnly = cleanPhone.replace(/\+/g, '');

    // Check max length: 12 digits
    if (digitsOnly.length > 12) {
      setPhoneError("Phone number cannot exceed 12 digits");
      return false;
    }

    // Phone numbers must have at least 10 digits
    if (digitsOnly.length < 10) {
      setPhoneError("Phone number must be at least 10 digits");
      return false;
    }

    // SA mobile prefixes (after removing country code)
    const validMobilePrefixes = ['060', '061', '062', '063', '064', '065', '066', '067', '068', '069', 
                                  '070', '071', '072', '073', '074', '075', '076', '077', '078', '079',
                                  '080', '081', '082', '083', '084', '085', '086', '087', '088', '089'];

    // Local format: 0XXXXXXXXX (10 digits starting with 06, 07, or 08)
    if (cleanPhone.match(/^0[6-8]\d{8}$/)) {
      const prefix = cleanPhone.substring(0, 3);
      if (validMobilePrefixes.includes(prefix)) {
        return true;
      }
      setPhoneError("Invalid SA mobile prefix. Must start with 06, 07, or 08");
      return false;
    }

    // International format: +27XXXXXXXXX or 27XXXXXXXXX (11-12 digits)
    if (cleanPhone.match(/^(\+27|27)[6-8]\d{8}$/)) {
      const localPart = cleanPhone.replace(/^(\+27|27)/, '0');
      const prefix = localPart.substring(0, 3);
      if (validMobilePrefixes.includes(prefix)) {
        return true;
      }
      setPhoneError("Invalid SA mobile prefix. Must start with 06, 07, or 08");
      return false;
    }

    setPhoneError("Please enter a valid SA mobile number (e.g., 0712345678 or +27712345678)");
    return false;
  };

  const checkPhoneDuplicate = async (phoneNumber: string): Promise<boolean> => {
    try {
      const response = await fetch('/api/check-phone-number', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phoneNumber })
      });

      const data = await response.json();

      if (!data.available) {
        setPhoneError(data.message || "This phone number is already registered with another account");
        return false;
      }

      return true;
    } catch (error) {
      console.error('[PHONE CHECK] Error checking phone availability:', error);
      setPhoneError("Unable to verify phone number. Please try again.");
      return false;
    }
  };

  const handleSavePhone = async () => {
    if (!phone.trim()) {
      setPhoneError("Phone number is required");
      return;
    }

    // Validate phone format
    if (!validatePhoneNumber(phone)) {
      return;
    }

    // Check for duplicate
    const isAvailable = await checkPhoneDuplicate(phone);
    if (!isAvailable) {
      return;
    }

    setIsSavingPhone(true);

    try {
      // Save phone number to user profile
      const response = await apiRequest("PATCH", "/api/user/profile", {
        body: { phone }
      });

      if (response.ok) {
        console.log('[PHONE GUARD] Phone saved, immediately showing OTP modal and sending SMS');

        // CRITICAL: Show OTP modal IMMEDIATELY before any delays
        console.log('[PHONE GUARD] Showing OTP modal immediately');
        setShowOTPModal(true);

        // Close phone input modal immediately after showing OTP modal
        console.log('[PHONE GUARD] Closing phone input modal - OTP modal is now visible');
        setShowPhoneInput(false);

        // Phone saved successfully, proceed to verification screen without auto-sending
        toast({
          title: "Phone Number Saved",
          description: "Click 'Send Code' to receive your verification code.",
          variant: "default",
        });
      } else {
      const data = await response.json();
      const errorMessage = data.message || data.error || "Failed to save phone number";
      setPhoneError(errorMessage);
      toast({
        title: "Save Failed",
        description: errorMessage,
        variant: "destructive",
      });
      }
    } catch (error) {
      console.error('[PHONE SAVE] Error saving phone:', error);
      setPhoneError("Failed to save phone number. Please try again.");
      toast({
        title: "Error",
        description: "Failed to save phone number. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingPhone(false);
    }
  };

  // Skip verification for admin users
  if (user && user.role === 'admin') {
    return <>{children}</>;
  }

  // Check if user needs phone verification
  const isGoogleUser = user?.supabaseUser?.app_metadata?.provider === 'google';
  const profileComplete = (user?.profileCompletion ?? 0) >= 30;
  const shouldShowVerification = isGoogleUser ? profileComplete : true;
  const needsPhoneVerification = user && user.phone && !user.phoneVerified && shouldShowVerification;

  // Render children with modals overlaid (allows both modals to be shown simultaneously during transition)
  return (
    <>
      {children}

      {/* Phone Input Modal - Non-dismissible (REQUIRED) - Shows when user has no phone */}
      {showPhoneInput && user && !user.phone && (
        <Dialog open={showPhoneInput}>
          <DialogContent
            className="sm:max-w-md"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="text-center space-y-4 mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Phone Number Required
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Please enter your phone number to continue. You'll receive a verification code.
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="phone-input">Phone Number</Label>
                <Input
                  id="phone-input"
                  type="tel"
                  placeholder="27712345678 or 0712345678"
                  value={phone}
                  onChange={(e) => {
                    // Sanitize: only allow digits, +, spaces, and dashes
                    const sanitized = e.target.value.replace(/[^\d+\s\-]/g, '');
                    setPhone(sanitized);
                    setPhoneError("");
                  }}
                  onBlur={(e) => {
                    if (e.target.value.trim()) {
                      validatePhoneNumber(e.target.value);
                    }
                  }}
                  className={phoneError ? "border-red-300 focus:border-red-500" : ""}
                  disabled={isSavingPhone}
                  data-testid="input-phone-verification"
                />
                {phoneError && (
                  <p className="text-xs text-red-600">{phoneError}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  SA mobile: 10-12 digits, must start with 06, 07, or 08
                </p>
              </div>

              <Button
                className="w-full"
                onClick={handleSavePhone}
                disabled={isSavingPhone || !phone.trim()}
                data-testid="button-save-phone"
              >
                {isSavingPhone ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Continue"
                )}
              </Button>
            </div>

            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={signOut}
                disabled={isSavingPhone}
                data-testid="button-logout-phone-input"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Phone Verification Modal - Non-dismissible (REQUIRED) - Shows when user has phone but not verified OR when transitioning from phone input */}
      {(needsPhoneVerification || showOTPModal) && (
        <Dialog open={showOTPModal}>
          <DialogContent
            className="sm:max-w-md"
            onPointerDownOutside={(e) => e.preventDefault()}
            onEscapeKeyDown={(e) => e.preventDefault()}
          >
            <div className="text-center space-y-4 mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                Phone Verification Required
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Verify your phone number to secure your account.
              </p>
            </div>
            <OTPVerification
              phone={user?.phone || phone || ""}
              onVerified={handleVerified}
            />
            <div className="mt-4 pt-4 border-t">
              <Button
                variant="outline"
                className="w-full"
                onClick={signOut}
                data-testid="button-logout-verification"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Log Out
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
